import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Menu,
  MenuItem,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client/react";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { GET_STUDENT_BY_ID, GET_STUDENT_LIST, ME } from "../graphql/query";
import { CHANGE_PASSWORD, UPDATE_PROFILE, UPDATE_STUDENT } from "../graphql/mutation";
import CustomSnackbar from "../components/Snackbar";
import DeleteAccountSection from "../components/DeleteAccountSection";
import AppDatePicker from "../components/AppDatePicker";
import { resizeFile } from "../utils/imageResizer";
import { REGEX } from "../utils/const";
import CloseIcon from "@mui/icons-material/Close";
import { countries, getEmojiFlag, TCountryCode } from "countries-list";
import * as Flags from "country-flag-icons/react/3x2";
import {
  getPhoneValidationMessage as validatePhoneNumber,
  getNationalNumber,
  PHONE_INPUT_MAX_DIGITS,
} from "../utils/phoneValidation";

type SnackbarState = "success" | "error";

type FormState = {
  name: string;
  email: string;
  phone: string;
  additionalPhone: string;
  address: string;
  country: string;
  postcode: string;
  dateOfBirth: string;
};

type PasswordState = {
  newPassword: string;
  confirmPassword: string;
};

type PasswordErrorState = {
  newPassword: string;
  confirmPassword: string;
};

type PasswordVisibilityState = {
  newPassword: boolean;
  confirmPassword: boolean;
};

type PhoneErrorState = {
  phone: string;
  additionalPhone: string;
};

type ProfileField = {
  key: keyof FormState;
  label: string;
};

const EMPTY_VALUE = "Not added yet";
const PASSWORD_REQUIRED_MESSAGE = "Password is required";
const PASSWORD_LENGTH_MESSAGE = "Password must be at least 8 characters";
const PASSWORD_UPPERCASE_MESSAGE = "Password must contain atleast one uppercase letter";
const PASSWORD_NUMBER_MESSAGE = "Password must contain atleast one number";
const PASSWORD_SPECIAL_CHARACTER_MESSAGE = "Password must contain atleast one special character";
const PASSWORD_CONFIRM_REQUIRED_MESSAGE = "Confirm password is required";
const PASSWORD_MISMATCH_MESSAGE = "Passwords do not match";

const getCountryDialCode = (countryName: string) => {
  if (!countryName) return "";

  const matchedCountry = Object.entries(countries).find(
    ([, country]) => country.name === countryName,
  );

  return matchedCountry?.[1]?.phone?.[0] || "";
};

// Phone here is optional (empty is allowed); the shared libphonenumber-js util returns
// "" for empty and does the accurate per-country validation otherwise.
const getPhoneValidationMessage = (phone: string, countryName: string) =>
  validatePhoneNumber(phone, countryName);

const PROFILE_PHOTO_LABEL = "Update profile photo";
const EDIT_PROFILE_PHOTO_LABEL = "Change photo";
const REMOVE_PROFILE_PHOTO_LABEL = "Remove photo";
const MAX_PROFILE_PHOTO_SIZE = 5 * 1024 * 1024;
const ALLOWED_PROFILE_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const getPhotoSrc = (photo?: string | null) => {
  if (!photo) return "";
  return photo.startsWith("data:") ? photo : `data:image/*;base64,${photo}`;
};

const getSettingsPasswordError = (
  name: "newPassword" | "confirmPassword",
  value: string,
  passwordData: PasswordState,
) => {
  if (name === "newPassword") {
    if (!value.trim()) return PASSWORD_REQUIRED_MESSAGE;
    if (value.length < 8) return PASSWORD_LENGTH_MESSAGE;
    if (!REGEX.UPPERCASE.test(value)) return PASSWORD_UPPERCASE_MESSAGE;
    if (!REGEX.NUMBER.test(value)) return PASSWORD_NUMBER_MESSAGE;
    if (!REGEX.SPECIAL_CHAR.test(value)) return PASSWORD_SPECIAL_CHARACTER_MESSAGE;
    return "";
  }

  if (!value.trim()) return PASSWORD_CONFIRM_REQUIRED_MESSAGE;
  if (passwordData.newPassword !== value) return PASSWORD_MISMATCH_MESSAGE;
  return "";
};

const AccountPage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data, refetch } = useQuery(ME); //fetches the currently logged-in user's profile

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const baseUser = data?.me;
  const userRole = (baseUser?.role || storedUser?.role || "").toUpperCase();
  const isStudent = userRole === "USER";
  const userEmail = baseUser?.email || storedUser?.email || "";

  const { data: studentListData } = useQuery(GET_STUDENT_LIST, {
    variables: {
      page: 1,
      perPage: 100,
      search: userEmail,
    },
    skip: !isStudent || !userEmail,
    fetchPolicy: "network-only",
  });

  const studentResults = studentListData?.getStudentList?.data || [];
  const matchedStudent = isStudent
    ? studentResults.find(
      (s: any) =>
        s.email?.trim().toLowerCase() === userEmail.trim().toLowerCase()
    ) || studentResults[0] || null
    : null;

  const studentId = matchedStudent?.id;

  const { data: studentProfileData } = useQuery(GET_STUDENT_BY_ID, {
    variables: { id: studentId },
    skip: !isStudent || !studentId,
    fetchPolicy: "network-only",
  });

  const studentProfile = studentProfileData?.getStudentById?.data;
  const storedUserPhoto = storedUser?.uploadPhoto || "";

  const profile = {
    name: isStudent
      ? studentProfile?.fullName ||
      matchedStudent?.fullName ||
      baseUser?.fullname ||
      storedUser?.fullname ||
      "User"
      : baseUser?.fullname || storedUser?.fullname || "User",

    email: isStudent
      ? studentProfile?.email ||
      matchedStudent?.email ||
      baseUser?.email ||
      storedUser?.email ||
      "-"
      : baseUser?.email || storedUser?.email || "-",

    // Display the national digits only — the dial code is shown separately in the
    // country selector, so it must not be duplicated inside the input.
    phone: isStudent
      ? getNationalNumber(
        studentProfile?.phoneNumber || matchedStudent?.phoneNumber || "",
        baseUser?.country || "",
      )
      : getNationalNumber(baseUser?.phoneNumber || "", baseUser?.country || ""),

    additionalPhone: isStudent
      ? getNationalNumber(
        studentProfile?.additionalPhoneNumber || baseUser?.additionalContact || "",
        baseUser?.country || "",
      )
      : getNationalNumber(baseUser?.additionalContact || "", baseUser?.country || ""),
    address: isStudent
      ? studentProfile?.address || matchedStudent?.address || ""
      : baseUser?.address || "",

    country: baseUser?.country || "",

    postcode: baseUser?.postcode || "",

    dateOfBirth: isStudent
      ? studentProfile?.dateOfBirth || matchedStudent?.dateOfBirth || ""
      : "",
  };

  const countryOptions = useMemo(
    () =>
      Object.entries(countries)
        .map(([code, country]) => ({
          code: code as TCountryCode,
          name: country.name,
          emoji: getEmojiFlag(code as TCountryCode),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const getCountryOptionByName = (countryName: string) =>
    countryOptions.find((country) => country.name === countryName) || null;

  const [updateProfile, { loading: updatingProfile }] = useMutation(
    UPDATE_PROFILE,
    {
      refetchQueries: [{ query: ME }],
      awaitRefetchQueries: true,
    }
  );

  const [updateStudent, { loading: updatingStudent }] = useMutation(
    UPDATE_STUDENT,
    {
      refetchQueries:
        isStudent && userEmail
          ? [
            { query: ME },
            {
              query: GET_STUDENT_LIST,
              variables: { page: 1, perPage: 100, search: userEmail },
            },
            ...(studentId
              ? [{ query: GET_STUDENT_BY_ID, variables: { id: studentId } }]
              : []),
          ]
          : [{ query: ME }],
    }
  );

  const [changePassword, { loading: changingPassword }] = useMutation(CHANGE_PASSWORD);

  const [openEdit, setOpenEdit] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);
  const [formData, setFormData] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    additionalPhone: "",
    address: "",
    country: "",
    postcode: "",
    dateOfBirth: "",
  });
  const [passwordData, setPasswordData] = useState<PasswordState>({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrorState>({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] =
    useState<PasswordVisibilityState>({
      newPassword: false,
      confirmPassword: false,
    });
  const [phoneErrors, setPhoneErrors] = useState<PhoneErrorState>({
    phone: "",
    additionalPhone: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState<SnackbarState>("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showEmailHelper, setShowEmailHelper] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentPhotoPreview, setCurrentPhotoPreview] = useState("");
  const [editPhotoPreview, setEditPhotoPreview] = useState("");
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [removePhotoPending, setRemovePhotoPending] = useState(false);
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryFieldMenuAnchor, setCountryFieldMenuAnchor] = useState<null | HTMLElement>(null);
  const [countryFieldSearch, setCountryFieldSearch] = useState("");

  const updating = updatingProfile || updatingStudent;
  const savedPhotoSrc = getPhotoSrc(baseUser?.uploadPhoto || storedUserPhoto);
  const profilePhotoSrc = currentPhotoPreview || savedPhotoSrc;

  const profileFields: ProfileField[] = isStudent
    ? [
      { key: "phone", label: "Phone" },
      { key: "dateOfBirth", label: "Date of birth" },
      { key: "address", label: "Address" },
      { key: "additionalPhone", label: "Additional phone" },
    ]
    : [
      { key: "address", label: "Address" },
      { key: "country", label: "Country" },
      { key: "postcode", label: "Postcode" },
      { key: "phone", label: "Phone" },
      { key: "additionalPhone", label: "Additional phone" },
    ];

  const formatValue = (value: string) =>
    value && value.trim() ? value : EMPTY_VALUE;

  const filteredCountryOptions = useMemo(() => {
    const search = countrySearch.trim().toLowerCase();
    if (!search) return countryOptions;

    return countryOptions.filter((country) => {
      const dialCode = getCountryDialCode(country.name);
      return (
        country.name.toLowerCase().includes(search) ||
        country.code.toLowerCase().includes(search) ||
        String(dialCode).includes(search.replace("+", ""))
      );
    });
  }, [countryOptions, countrySearch]);

  const filteredCountryFieldOptions = useMemo(() => {
    const search = countryFieldSearch.trim().toLowerCase();
    if (!search) return countryOptions;

    return countryOptions.filter((country) => {
      const dialCode = getCountryDialCode(country.name);
      return (
        country.name.toLowerCase().includes(search) ||
        country.code.toLowerCase().includes(search) ||
        String(dialCode).includes(search.replace("+", ""))
      );
    });
  }, [countryOptions, countryFieldSearch]);


  const validateProfilePhoto = (file: File) => {
    if (!ALLOWED_PROFILE_PHOTO_TYPES.includes(file.type)) {
      return "Please upload a JPG or PNG image";
    }
    if (file.size > MAX_PROFILE_PHOTO_SIZE) {
      return "Profile photo must be 5 MB or smaller";
    }
    return "";
  };

  const prepareProfilePhoto = async (file: File) => {
    try {
      return await resizeFile(file, 600, 600, "JPEG", 70);
    } catch (error) {
      console.error("Profile photo resize failed:", error);
      return file;
    }
  };

  const persistProfilePhoto = async (file: File, successMessage: string) => {
    setUploadingPhoto(true);

    try {
      const { data: response } = await updateProfile({
        variables: {
          input: {
            uploadPhoto: file,
          },
        },
      });

      if (!response?.updateProfile?.success) {
        throw new Error(
          response?.updateProfile?.message || "Failed to update profile photo"
        );
      }

      const nextPhotoSrc = getPhotoSrc(
        response.updateProfile.user?.uploadPhoto || ""
      );

      if (nextPhotoSrc) {
        setCurrentPhotoPreview(nextPhotoSrc);
      }

      await refetch();   // refresh profile data

      setSnackbarType("success");
      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Profile photo update failed:", error);
      setSnackbarType("error");
      setSnackbarMessage("Failed to update profile photo");
      setSnackbarOpen(true);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleMainPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationMessage = validateProfilePhoto(file);
    if (validationMessage) {
      setSnackbarType("error");
      setSnackbarMessage(validationMessage);
      setSnackbarOpen(true);
      e.target.value = "";
      return;
    }

    const preparedFile = await prepareProfilePhoto(file);
    await persistProfilePhoto(preparedFile, "Profile photo updated successfully");
    e.target.value = "";
  };

  const handleEditPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationMessage = validateProfilePhoto(file);
    if (validationMessage) {
      setSnackbarType("error");
      setSnackbarMessage(validationMessage);
      setSnackbarOpen(true);
      e.target.value = "";
      return;
    }

    const preparedFile = await prepareProfilePhoto(file);
    setRemovePhotoPending(false);
    setEditPhotoFile(preparedFile);
    setEditPhotoPreview(URL.createObjectURL(preparedFile));
    e.target.value = "";
  };

  const handleRemoveEditPhoto = () => {
    setRemovePhotoPending(true);
    setEditPhotoFile(null);
    setEditPhotoPreview("");
  };

  const handleEdit = () => {
    setFormData({
      ...profile,
      phone: isStudent
        ? studentProfile?.phoneNumber || matchedStudent?.phoneNumber || ""
        : baseUser?.phoneNumber || "",
    });
    setPhoneErrors({ phone: "", additionalPhone: "" });
    setRemovePhotoPending(false);
    setEditPhotoFile(null);
    setEditPhotoPreview(profilePhotoSrc);
    setOpenEdit(true);
  };

  const handleClose = () => {
    setOpenEdit(false);
    setShowEmailHelper(false);
    setPhoneErrors({ phone: "", additionalPhone: "" });
    setEditPhotoFile(null);
    setEditPhotoPreview("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (isStudent && (name === "phone" || name === "additionalPhone")) {
      const digitsOnly = value.replace(/\D/g, "").slice(0, PHONE_INPUT_MAX_DIGITS);
      const nextPhoneError =
        name === "phone" || name === "additionalPhone"
          ? getPhoneValidationMessage(digitsOnly, formData.country)
          : "";
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
      setPhoneErrors((prev) => ({
        ...prev,
        [name]: nextPhoneError,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "phone" || name === "additionalPhone") {
      setPhoneErrors((prev) => ({
        ...prev,
        [name]: getPhoneValidationMessage(value, formData.country),
      }));
    }
  };

  const handleCountryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCountryMenuAnchor(event.currentTarget);
  };

  const handleCountryMenuClose = () => {
    setCountryMenuAnchor(null);
    setCountrySearch("");
  };

  const handleCountrySelect = (countryName: string) => {
    const nextPhoneError = getPhoneValidationMessage(formData.phone, countryName);
    const nextAdditionalPhoneError = getPhoneValidationMessage(
      formData.additionalPhone,
      countryName,
    );
    setFormData((prev) => ({
      ...prev,
      country: countryName,
    }));
    setPhoneErrors({
      phone: nextPhoneError,
      additionalPhone: nextAdditionalPhoneError,
    });
    handleCountryMenuClose();
  };

  const handleCountryFieldMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCountryFieldMenuAnchor(event.currentTarget);
  };

  const handleCountryFieldMenuClose = () => {
    setCountryFieldMenuAnchor(null);
    setCountryFieldSearch("");
  };

  const handleCountryFieldSelect = (countryName: string) => {
    const nextPhoneError = getPhoneValidationMessage(formData.phone, countryName);
    const nextAdditionalPhoneError = getPhoneValidationMessage(
      formData.additionalPhone,
      countryName,
    );
    setFormData((prev) => ({
      ...prev,
      country: countryName,
    }));
    setPhoneErrors({
      phone: nextPhoneError,
      additionalPhone: nextAdditionalPhoneError,
    });
    handleCountryFieldMenuClose();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextPasswordData = {
      ...passwordData,
      [name]: value,
    };

    setPasswordData(nextPasswordData);
    setPasswordErrors((prev) => ({
      ...prev,
      [name]: getSettingsPasswordError(
        name as "newPassword" | "confirmPassword",
        value,
        nextPasswordData,
      ),
    }));
  };

  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name !== "newPassword" && name !== "confirmPassword") {
      return;
    }

    setPasswordErrors((prev) => ({
      ...prev,
      [name]: getSettingsPasswordError(name, value, passwordData),
    }));
  };

  const togglePasswordVisibility = (field: keyof PasswordVisibilityState) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validatePasswordForm = () => {
    const errors: PasswordErrorState = {
      newPassword: getSettingsPasswordError(
        "newPassword",
        passwordData.newPassword,
        passwordData,
      ),
      confirmPassword: getSettingsPasswordError(
        "confirmPassword",
        passwordData.confirmPassword,
        passwordData,
      ),
    };

    setPasswordErrors(errors);
    return !errors.newPassword && !errors.confirmPassword;
  };

  const isPasswordUpdateDisabled =
    changingPassword ||
    !passwordData.newPassword.trim() ||
    !passwordData.confirmPassword.trim();

  const passwordFieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 52,
      borderRadius: "10px",
      backgroundColor: "#FFFFFF",
      "& input": {
        fontSize: 14,
        padding: "14px 16px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#C9CED6",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#60AF3D",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#60AF3D",
        borderWidth: "1px",
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#EF4444",
      },
      "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#EF4444",
      },
      "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#EF4444",
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "4px",
      marginTop: "8px",
      fontSize: 12,
      color: "#EF4444",
      fontWeight: 400,
    },
  };

  const handleSave = async () => {
    try {
      const phoneValidationMessage = getPhoneValidationMessage(
        formData.phone,
        formData.country,
      );
      if (phoneValidationMessage) {
        setPhoneErrors((prev) => ({ ...prev, phone: phoneValidationMessage }));
        return;
      }

      const additionalPhoneValidationMessage = getPhoneValidationMessage(
        formData.additionalPhone,
        formData.country,
      );
      if (additionalPhoneValidationMessage) {
        setPhoneErrors((prev) => ({
          ...prev,
          additionalPhone: additionalPhoneValidationMessage,
        }));
        return;
      }

      setPhoneErrors({ phone: "", additionalPhone: "" });

      // Update student specific data
      if (isStudent && studentId) {
        const { data: studentResponse } = await updateStudent({
          variables: {
            id: studentId,
            input: {
              fullName: formData.name,
              phoneNumber: formData.phone.replace(/\D/g, ""),
              additionalPhoneNumber: formData.additionalPhone.replace(/\D/g, ""),
              dateOfBirth: formData.dateOfBirth?.trim() || undefined,
              address: formData.address?.trim() || undefined,
            },
          },
        });

        if (!studentResponse?.updateStudent?.success) {
          throw new Error(
            studentResponse?.updateStudent?.message || "Failed to update student profile"
          );
        }
      }

      const profileInput: Record<string, unknown> = {
        fullname: formData.name,
        phoneNumber: formData.phone.replace(/\D/g, ""),
        address: formData.address?.trim() || undefined,
        additionalContact: formData.additionalPhone.replace(/\D/g, ""),
      };

      if (formData.country?.trim()) {
        profileInput.country = formData.country;
      }

      if (formData.postcode?.trim()) {
        profileInput.postcode = formData.postcode;
      }

      if (editPhotoFile) {
        profileInput.uploadPhoto = editPhotoFile;
      } else if (removePhotoPending) {
        profileInput.removeUploadPhoto = true;
      }

      const { data: response } = await updateProfile({
        variables: { input: profileInput },
      });

      if (!response?.updateProfile?.success) {
        throw new Error(
          response?.updateProfile?.message || "Failed to update profile"
        );
      }

      await refetch(); // refresh the profile data
      setCurrentPhotoPreview("");

      setOpenEdit(false);
      setEditPhotoFile(null);
      setEditPhotoPreview("");
      setRemovePhotoPending(false);

      setSnackbarType("success");
      setSnackbarMessage("Profile updated successfully");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Profile Update Failed:", error);

      setSnackbarType("error");
      setSnackbarMessage(error?.message || "Failed to update profile");
      setSnackbarOpen(true);
    }
  };

  const handlePasswordSave = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    try {
      const { data: response } = await changePassword({
        variables: { newPassword: passwordData.newPassword },
      });

      if (!response?.changePassword?.success) {
        setSnackbarType("error");
        setSnackbarMessage(response?.changePassword?.message || "Failed to change password");
        setSnackbarOpen(true);
        return;
      }

      setOpenPassword(false);
      setPasswordData({ newPassword: "", confirmPassword: "" });
      setPasswordErrors({ newPassword: "", confirmPassword: "" });
      setSnackbarType("success");
      setSnackbarMessage(response.changePassword.message || "Password updated successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Change Password Failed:", error);
      setSnackbarType("error");
      setSnackbarMessage("Failed to change password");
      setSnackbarOpen(true);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography
        variant="h5"
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          color: "#152C1A",
          mb: 2.5,
        }}
      >
        My Account
      </Typography>

      <Card
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "18px",
          border: "1px solid #E6ECE2",
          boxShadow: "0 8px 24px rgba(25, 47, 17, 0.05)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="flex-start" mb={3}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: { xs: 2, md: 3 },
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: 2.5,
                  flexDirection: { xs: "column", sm: "row" },
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 120,
                  }}
                >
                  <Avatar
                    src={profilePhotoSrc || undefined}
                    sx={{
                      width: 88,
                      height: 88,
                      fontSize: 34,
                      fontWeight: 700,
                      bgcolor: "#60AF3D",
                    }}
                  >
                    {profile.name?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddAPhotoOutlinedIcon />}
                    disabled={uploadingPhoto}
                    sx={{
                      borderColor: "#CFE7C3",
                      color: "#60AF3D",
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "999px",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#60AF3D",
                        backgroundColor: "#F6FBF2",
                      },
                    }}
                  >
                    {uploadingPhoto ? "Uploading..." : PROFILE_PHOTO_LABEL}
                    <input
                      hidden
                      accept="image/png,image/jpeg,image/jpg"
                      type="file"
                      onChange={handleMainPhotoChange}
                    />
                  </Button>

                  <Typography sx={{ fontSize: 12, color: "#7A7F87", textAlign: "center" }}>
                    PNG or JPG up to 5 MB
                  </Typography>
                </Box>

                <Box sx={{ pt: { xs: 0, md: 0.5 } }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 18, md: 20 },
                      fontWeight: 700,
                      color: "#1B1D1F",
                      mb: 0.5,
                    }}
                  >
                    {profile.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#7A7F87",
                      mb: 1.25,
                    }}
                  >
                    {isStudent ? "Student" : userRole.replace(/_/g, " ")}
                  </Typography>

                  <Box sx={{ color: "#24282D" }}>
                    <Typography sx={{ fontSize: 15 }}>
                      <Box component="span" sx={{ color: "#6C737F", mr: 0.75 }}>
                        Email:
                      </Box>
                      {profile.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 0.75,
                  width: { xs: "100%", md: "auto" },
                  pt: { xs: 0.5, md: 0.25 },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setOpenPassword(true)}
                  sx={{
                    backgroundColor: "#60AF3D",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#4F9631",
                      boxShadow: "none",
                    },
                  }}
                >
                  Change password
                </Button>

                <Button
                  variant="contained"
                  onClick={handleEdit}
                  sx={{
                    backgroundColor: "#60AF3D",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#4F9631",
                      boxShadow: "none",
                    },
                  }}
                >
                  Edit
                </Button>

              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Card
              sx={{
                borderRadius: "16px",
                border: "1px solid #E4E7EC",
                boxShadow: "none",
                overflow: "hidden",
                backgroundColor: "#FFFFFF",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 1.5,
                  borderBottom: "1px solid #EDEFF2",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#22262B",
                  }}
                >
                  Profile Details
                </Typography>
              </Box>

              <Grid container spacing={0}>
                {profileFields.map((field) => (
                  <Grid
                    key={field.key}
                    size={{ xs: 12, sm: 6, md: isStudent ? 4 : 6 }}
                    sx={{
                      px: { xs: 2, md: 2.5 },
                      py: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#8A9099",
                        mb: 0.75,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: profile[field.key] ? "#1F242A" : "#9BA3AE",
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                        whiteSpace: field.key === "address" ? "pre-wrap" : "normal",
                      }}
                    >
                      {formatValue(profile[field.key])}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>


        </Grid>
      </Card>

      <Dialog
        open={openEdit}
        onClose={handleClose}
        fullWidth
        fullScreen={isSmallScreen}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            boxShadow: { xs: "none", sm: "0 18px 48px rgba(16, 24, 40, 0.18)" },
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 2, sm: 3.5 },
            pt: { xs: 2, sm: 2.5 },
          }}
        >
          Edit Profile

          <IconButton
            onClick={handleClose}
            sx={{
              color: "#98A2B3",
              "&:hover": {
                backgroundColor: "#F2F4F7",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "grid",
            gap: 1.75,
            pt: 1.5,
            px: { xs: 2, sm: 3.5 },
            pb: 1.5,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
              pt: 0.5,
              pb: 1,
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={editPhotoPreview || undefined}
                sx={{
                  width: 92,
                  height: 92,
                  fontSize: 34,
                  fontWeight: 700,
                  bgcolor: "#60AF3D",
                }}
              >
                {profile.name?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
              <IconButton
                component="label"
                aria-label="Change profile photo"
                sx={{
                  position: "absolute",
                  right: -2,
                  bottom: -2,
                  width: 34,
                  height: 34,
                  bgcolor: "#60AF3D",
                  color: "#FFFFFF",
                  border: "3px solid #FFFFFF",
                  "&:hover": { bgcolor: "#4F9631" },
                }}
              >
                <AddAPhotoOutlinedIcon sx={{ fontSize: 18 }} />
                <input
                  hidden
                  accept="image/png,image/jpeg,image/jpg"
                  type="file"
                  onChange={handleEditPhotoChange}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<AddAPhotoOutlinedIcon />}
                sx={{
                  borderColor: "#CFE7C3",
                  color: "#60AF3D",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "999px",
                  "&:hover": {
                    borderColor: "#60AF3D",
                    backgroundColor: "#F6FBF2",
                  },
                }}
              >
                {EDIT_PROFILE_PHOTO_LABEL}
                <input
                  hidden
                  accept="image/png,image/jpeg,image/jpg"
                  type="file"
                  onChange={handleEditPhotoChange}
                />
              </Button>

              <Button
                variant="outlined"
                onClick={handleRemoveEditPhoto}
                disabled={!editPhotoPreview}
                sx={{
                  borderColor: editPhotoPreview ? "#F0C7C7" : "#EAECF0",
                  color: editPhotoPreview ? "#C95C5C" : "#98A2B3",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "999px",
                  "&:hover": {
                    borderColor: editPhotoPreview ? "#C95C5C" : "#EAECF0",
                    backgroundColor: editPhotoPreview ? "#FFF5F5" : "transparent",
                  },
                }}
              >
                {REMOVE_PROFILE_PHOTO_LABEL}
              </Button>
            </Box>

            <Typography sx={{ fontSize: 12, color: "#7A7F87" }}>
              PNG or JPG up to 5 MB
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "#EAECF0" }} />

          <Box sx={{ display: "grid", gap: 2.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonOutlineOutlinedIcon sx={{ color: "#60AF3D", fontSize: 18 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.8, color: "#98A2B3" }}>
                PERSONAL INFORMATION
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                  Full Name
                </Typography>
                <TextField
                  name="name"
                  placeholder="Full Name"
                  fullWidth
                  size="small"
                  value={formData.name}
                  onChange={handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                  Email Address
                </Typography>
                <TextField
                  name="email"
                  placeholder="Email Address"
                  fullWidth
                  size="small"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setShowEmailHelper(true)}
                  onBlur={() => setShowEmailHelper(false)}
                  helperText={showEmailHelper ? "Email cannot be edited" : undefined}
                  InputProps={{ readOnly: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F5F7F4",
                    },
                    "& .MuiInputBase-input": { WebkitTextFillColor: "#5F6B66" },
                  }}
                />
              </Grid>

              {isStudent ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                    Date of Birth
                  </Typography>
                  <AppDatePicker
                    value={formData.dateOfBirth}
                    onChange={(v) =>
                      handleChange({ target: { name: "dateOfBirth", value: v } } as any)
                    }
                  />
                </Grid>
              ) : null}
            </Grid>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <PhoneOutlinedIcon sx={{ color: "#60AF3D", fontSize: 18 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 900, letterSpacing: 0.8, color: "#98A2B3" }}>
                CONTACT & LOCATION
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                  Phone Number
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 40,
                    border: "1px solid #D0D5DD",
                    borderColor: phoneErrors.phone ? "#EF4444" : "#D0D5DD",
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                    overflow: "hidden",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: "rgba(0, 0, 0, 0.87)",
                    },
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 1px #1976d2",
                    },
                  }}
                >
                  <Box
                    onClick={handleCountryMenuOpen}
                    sx={{
                      width: "fit-content",
                      maxWidth: { xs: 104, sm: 112 },
                      px: 1.25,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      borderRight: "1px solid #E4E7EC",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {(() => {
                      const selectedCountry =
                        countryOptions.find((country) => country.name === formData.country) || null;
                      const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;
                      const dialCode = getCountryDialCode(formData.country);

                      return (
                        <>
                          <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {Flag ? (
                              <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                            ) : (
                              <Typography sx={{ fontSize: 13 }}>
                                {selectedCountry?.emoji || "🌐"}
                              </Typography>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: dialCode ? "#1D2939" : "#98A2B3" }}>
                            {dialCode ? `+${dialCode}` : "Code"}
                          </Typography>
                          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3" }} />
                        </>
                      );
                    })()}
                  </Box>

                  <TextField
                    name="phone"
                    placeholder="Phone Number"
                    fullWidth
                    size="small"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="standard"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: PHONE_INPUT_MAX_DIGITS,
                    }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      flex: "1 1 0",
                      minWidth: 0,
                      maxWidth: "100%",
                      "&.MuiFormControl-root": {
                        flex: "1 1 0",
                        minWidth: 0,
                        maxWidth: "100%",
                      },
                      "& .MuiInputBase-root": {
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        width: "100%",
                        minHeight: 40,
                        minWidth: 0,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: 16,
                        fontWeight: 400,
                        color: "#1D2939",
                        minWidth: 0,
                        width: "100%",
                        flex: 1,
                        padding: 0,
                      },
                    }}
                  />
                </Box>
                {phoneErrors.phone ? (
                  <Typography
                    sx={{
                      mt: 0.75,
                      ml: 0.5,
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: "#EF4444",
                    }}
                  >
                    {phoneErrors.phone}
                  </Typography>
                ) : null}

                <Menu
                  anchorEl={countryMenuAnchor}
                  open={Boolean(countryMenuAnchor)}
                  onClose={handleCountryMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      width: 320,
                      maxHeight: 380,
                      borderRadius: "16px",
                      border: "1px solid #D0D5DD",
                      boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
                      p: 0.5,
                    },
                  }}
                  MenuListProps={{ disablePadding: true }}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      value={countrySearch}
                      onChange={(event) => setCountrySearch(event.target.value)}
                      placeholder="Search"
                      fullWidth
                      size="small"
                      autoFocus
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                      InputProps={{
                        startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
                      }}
                    />
                  </Box>

                  <Box sx={{ maxHeight: 300, overflowY: "auto", pb: 0.5 }}>
                    {filteredCountryOptions.map((country) => {
                      const Flag = (Flags as any)[country.code];
                      return (
                        <MenuItem key={country.code} onClick={() => handleCountrySelect(country.name)} sx={{ py: 1.25, px: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {Flag ? (
                                <Flag style={{ width: 22, height: 16, borderRadius: "2px" }} />
                              ) : (
                                <span>{country.emoji}</span>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939" }}>
                              {country.name} (+{getCountryDialCode(country.name)})
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Box>
                </Menu>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                  Secondary Phone
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 40,
                    border: "1px solid #D0D5DD",
                    borderColor: phoneErrors.additionalPhone ? "#EF4444" : "#D0D5DD",
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                    overflow: "hidden",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: "rgba(0, 0, 0, 0.87)",
                    },
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 1px #1976d2",
                    },
                  }}
                >
                  <Box
                    onClick={handleCountryMenuOpen}
                    sx={{
                      width: "fit-content",
                      maxWidth: { xs: 104, sm: 112 },
                      px: 1.25,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      borderRight: "1px solid #E4E7EC",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {(() => {
                      const selectedCountry =
                        countryOptions.find((country) => country.name === formData.country) || null;
                      const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;
                      const dialCode = getCountryDialCode(formData.country);

                      return (
                        <>
                          <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {Flag ? (
                              <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                            ) : (
                              <Typography sx={{ fontSize: 13 }}>
                                {selectedCountry?.emoji || "🌐"}
                              </Typography>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: dialCode ? "#1D2939" : "#98A2B3" }}>
                            {dialCode ? `+${dialCode}` : "Code"}
                          </Typography>
                          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3" }} />
                        </>
                      );
                    })()}
                  </Box>

                  <TextField
                    name="additionalPhone"
                    placeholder="Secondary Phone"
                    fullWidth
                    size="small"
                    value={formData.additionalPhone}
                    onChange={handleChange}
                    variant="standard"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: PHONE_INPUT_MAX_DIGITS,
                    }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      flex: "1 1 0",
                      minWidth: 0,
                      maxWidth: "100%",
                      "&.MuiFormControl-root": {
                        flex: "1 1 0",
                        minWidth: 0,
                        maxWidth: "100%",
                      },
                      "& .MuiInputBase-root": {
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        width: "100%",
                        minHeight: 40,
                        minWidth: 0,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: 16,
                        fontWeight: 400,
                        color: "#1D2939",
                        minWidth: 0,
                        width: "100%",
                        flex: 1,
                        padding: 0,
                      },
                    }}
                  />
                </Box>
                {phoneErrors.additionalPhone ? (
                  <Typography
                    sx={{
                      mt: 0.75,
                      ml: 0.5,
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: "#EF4444",
                    }}
                  >
                    {phoneErrors.additionalPhone}
                  </Typography>
                ) : null}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                  <PlaceOutlinedIcon sx={{ color: "#60AF3D", fontSize: 18 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085" }}>
                    Street Address
                  </Typography>
                </Box>
                <TextField
                  name="address"
                  placeholder="Street Address"
                  fullWidth
                  size="small"
                  multiline
                  minRows={4}
                  value={formData.address}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px", alignItems: "flex-start",

                    }, "& .MuiInputBase-inputMultiline": {

                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                    },
                  }}
                />
              </Grid>

              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                    Country
                  </Typography>
                  <Box
                    onClick={handleCountryFieldMenuOpen}
                    sx={{
                      height: 40,
                      px: 1.5,
                      py: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      border: "1px solid #D0D5DD",
                      borderRadius: "12px",
                      backgroundColor: "#FFFFFF",
                      cursor: "pointer",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 0, 0, 0.23)",
                      },
                      "&:focus-within": {
                        borderColor: "#1976d2",
                        boxShadow: "0 0 0 1px #1976d2",
                      },
                    }}
                  >
                    {(() => {
                      const selectedCountry =
                        countryOptions.find((country) => country.name === formData.country) || null;
                      const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;

                      if (!selectedCountry) {
                        return (
                          <>
                            <Typography sx={{ color: "#98A2B3", fontWeight: 600, fontSize: 14 }}>
                              Select country
                            </Typography>
                            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "rgba(0, 0, 0, 0.54)" }} />
                          </>
                        );
                      }

                      return (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ display: "flex", alignItems: "center", width: 16, flexShrink: 0 }}>
                              {Flag ? (
                                <Flag style={{ width: "100%", height: "auto", borderRadius: "2px" }} />
                              ) : (
                                <span>{selectedCountry.emoji}</span>
                              )}
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1D2939", lineHeight: 1.2 }}>
                              {selectedCountry.name}
                            </Typography>
                          </Box>
                          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "rgba(0, 0, 0, 0.54)" }} />
                        </>
                      );
                    })()}
                  </Box>

                  <Menu
                    anchorEl={countryFieldMenuAnchor}
                    open={Boolean(countryFieldMenuAnchor)}
                    onClose={handleCountryFieldMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        width: 320,
                        maxHeight: 380,
                        borderRadius: "16px",
                        border: "1px solid #D0D5DD",
                        boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
                        p: 0.5,
                      },
                    }}
                    MenuListProps={{ disablePadding: true }}
                  >
                    <Box sx={{ p: 1 }}>
                      <TextField
                        value={countryFieldSearch}
                        onChange={(event) => setCountryFieldSearch(event.target.value)}
                        placeholder="Search"
                        fullWidth
                        size="small"
                        autoFocus
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        InputProps={{
                          startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
                        }}
                      />
                    </Box>

                    <Box sx={{ maxHeight: 300, overflowY: "auto", pb: 0.5 }}>
                      {filteredCountryFieldOptions.map((country) => {
                        const Flag = (Flags as any)[country.code];
                        return (
                          <MenuItem key={country.code} onClick={() => handleCountryFieldSelect(country.name)} sx={{ py: 1.25, px: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Box sx={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {Flag ? (
                                  <Flag style={{ width: 22, height: 16, borderRadius: "2px" }} />
                                ) : (
                                  <span>{country.emoji}</span>
                                )}
                              </Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939" }}>
                                {country.name} (+{getCountryDialCode(country.name)})
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Menu>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", mb: 0.75 }}>
                    Postcode
                  </Typography>
                  <TextField
                    name="postcode"
                    placeholder="Postcode"
                    fullWidth
                    size="small"
                    value={formData.postcode}
                    onChange={handleChange}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
              </>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3.5 },
            pb: { xs: 2, sm: 3.5 },
            pt: { xs: 1.5, sm: 2.5 },
            borderTop: "1px solid #EAECF0",
            backgroundColor: "#F8FAFC",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 1.5 },
            "& .MuiButton-root": {
              width: { xs: "100%", sm: "auto" },
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderColor: "#D0D5DD",
              color: "#475467",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updating}
            sx={{
              backgroundColor: "#60AF3D",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4F9631",
                boxShadow: "none",
              },
            }}
          >
            {updating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPassword}
        onClose={() => setOpenPassword(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 18px 48px rgba(16, 24, 40, 0.18)",
            maxWidth: 360,
            width: { xs: "calc(100% - 32px)", sm: "100%" },
            maxHeight: { xs: "calc(100% - 32px)", sm: "none" },
            m: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            pb: 0.5,
            pt: 3,
            textAlign: "center",
            color: "#60AF3D",
            letterSpacing: -0.3,
            position: "relative",
          }}
        >
          Change Password
          <IconButton
            aria-label="Close change password dialog"
            onClick={() => {
              setOpenPassword(false);
              setPasswordErrors({ newPassword: "", confirmPassword: "" });
            }}
            size="small"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#98A2B3",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 1.5,
            px: { xs: 2.5, sm: 4 },
            pb: { xs: 2.5, sm: 3 },
            overflowY: "auto",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 400,
              color: "#344054",
              textAlign: "center",
              lineHeight: 1.45,
              maxWidth: 340,
              mx: "auto",
              mb: 2,
            }}
          >
            Please enter your current password and<br /> choose a secure new one.
          </Typography>
          <Box sx={{ display: "grid", gap: 2.25 }}>
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#101828" }}>
                Password
              </Typography>
              <TextField
                placeholder="Password"
                name="newPassword"
                type={passwordVisibility.newPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => togglePasswordVisibility("newPassword")}
                        aria-label="Toggle new password visibility"
                        sx={{ mr: 0.5 }}
                      >
                        {passwordVisibility.newPassword ? (
                          <VisibilityOutlinedIcon sx={{ fontSize: 22, color: "#7A7A7A" }} />
                        ) : (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 22, color: "#7A7A7A" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={passwordFieldSx}
              />
            </Box>

            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#101828" }}>
                Confirm Password
              </Typography>
              <TextField
                placeholder="Confirm New Password"
                name="confirmPassword"
                type={passwordVisibility.confirmPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => togglePasswordVisibility("confirmPassword")}
                        aria-label="Toggle confirm password visibility"
                        sx={{ mr: 0.5 }}
                      >
                        {passwordVisibility.confirmPassword ? (
                          <VisibilityOutlinedIcon sx={{ fontSize: 22, color: "#7A7A7A" }} />
                        ) : (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 22, color: "#7A7A7A" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={passwordFieldSx}
              />
            </Box>

            <Button
              variant="contained"
              onClick={handlePasswordSave}
              disabled={isPasswordUpdateDisabled}
              fullWidth
              sx={{
                mt: 0.5,
                py: 1.25,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                backgroundColor: "#60AF3D",
                "&:hover": {
                  backgroundColor: "#4F9631",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E4E7EC",
                  color: "#98A2B3",
                },
              }}
            >
              {changingPassword ? "Updating..." : "Update"}
            </Button>

          </Box>
        </DialogContent>
      </Dialog>

      {isStudent && <DeleteAccountSection />}

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={4000}
      />
    </Box>
  );
};

export default AccountPage;
